import React from 'react';
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '@jem-open/jem-ui';

interface TablePaginationProps {
  page: number;
  pageCount: number;
  onPageChange: (page: number) => void;
}

// Build a compact list of page tokens with ellipses for large ranges, e.g.
// 1 … 4 5 6 … 12. Always keeps the first/last page and the current ±1 visible.
const getPageTokens = (
  page: number,
  pageCount: number,
): (number | 'gap')[] => {
  if (pageCount <= 7) {
    return Array.from({ length: pageCount }, (_, i) => i + 1);
  }
  const tokens: (number | 'gap')[] = [1];
  const start = Math.max(2, page - 1);
  const end = Math.min(pageCount - 1, page + 1);
  if (start > 2) tokens.push('gap');
  for (let p = start; p <= end; p += 1) tokens.push(p);
  if (end < pageCount - 1) tokens.push('gap');
  tokens.push(pageCount);
  return tokens;
};

const TablePagination = ({
  page,
  pageCount,
  onPageChange,
}: TablePaginationProps) => {
  if (pageCount <= 1) return null;

  const go = (target: number) => (e: React.MouseEvent) => {
    e.preventDefault();
    const clamped = Math.min(Math.max(target, 1), pageCount);
    if (clamped !== page) onPageChange(clamped);
  };

  const isFirst = page <= 1;
  const isLast = page >= pageCount;
  const disabledClass = 'pointer-events-none opacity-50';

  return (
    <Pagination className="mt-md justify-end">
      <PaginationContent>
        <PaginationItem>
          <PaginationPrevious
            href="#"
            size="default"
            onClick={go(page - 1)}
            aria-disabled={isFirst}
            className={isFirst ? disabledClass : undefined}
          />
        </PaginationItem>
        {getPageTokens(page, pageCount).map((token, i) =>
          token === 'gap' ? (
            // eslint-disable-next-line react/no-array-index-key
            <PaginationItem key={`gap-${i}`}>
              <PaginationEllipsis />
            </PaginationItem>
          ) : (
            <PaginationItem key={token}>
              <PaginationLink
                href="#"
                size="icon-sm"
                isActive={token === page}
                onClick={go(token)}
              >
                {token}
              </PaginationLink>
            </PaginationItem>
          ),
        )}
        <PaginationItem>
          <PaginationNext
            href="#"
            size="default"
            onClick={go(page + 1)}
            aria-disabled={isLast}
            className={isLast ? disabledClass : undefined}
          />
        </PaginationItem>
      </PaginationContent>
    </Pagination>
  );
};

export default TablePagination;
